<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Switch } from '@/components/shadcn'

type StyleType =
  | 'default'   // 기본 스타일
  | 'primary'   // 강조 스타일 (파랑)
  | 'success'   // 성공 스타일 (초록)
  | 'danger'    // 위험 스타일 (빨강)
  | 'sm'        // 작은 크기
  | 'lg'        // 큰 크기

const props = withDefaults(
  defineProps<{
    modelValue?: string
    disabled?: boolean
    name?: string
    id?: string
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
    /** 상태 라벨 노출 여부 */
    showStateLabel?: boolean
    /** 활성(Y) 상태 라벨 */
    activeLabel?: string
    /** 비활성(N) 상태 라벨 */
    inactiveLabel?: string
  }>(),
  {
    modelValue: 'N',
    disabled: false,
    styletype: 'default',
    showStateLabel: true,
    activeLabel: '활성',
    inactiveLabel: '비활성',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'change': [value: string]
}>()

/**
 * styletype -> class 매핑
 */
const UNCHECKED_BASE = [
  'data-[state=unchecked]:bg-secondary',
  'data-[state=unchecked]:border data-[state=unchecked]:border-border/60',
  'data-[state=unchecked]:hover:bg-secondary/80',
].join(' ')

const STYLE_PRESETS: Record<StyleType, { class: string }> = {
  default: { class: UNCHECKED_BASE },
  primary: {
    class: `${UNCHECKED_BASE} data-[state=checked]:bg-blue-500`,
  },
  success: {
    class: `${UNCHECKED_BASE} data-[state=checked]:bg-green-600`,
  },
  danger: {
    class: `${UNCHECKED_BASE} data-[state=checked]:bg-destructive`,
  },
  sm: {
    class: `${UNCHECKED_BASE} h-5 w-9`,
  },
  lg: {
    class: `${UNCHECKED_BASE} h-7 w-14`,
  },
}

/** 최종 바인딩 */
const computedClass = computed(() => {
  const styleKey = props.styletype || 'default'
  const preset = STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
  // disabled:opacity-100: shadcn Switch 내부의 disabled:opacity-50을 중화 — wrapper span에서 통합 관리
  return [preset?.class, props.class, 'disabled:opacity-100'].filter(Boolean).join(' ')
})

// 내부 상태로 boolean checked 관리
const internalChecked = ref<boolean>(props.modelValue === 'Y')

// modelValue 변경 감지하여 내부 상태 동기화
watch(
  () => props.modelValue,
  (newValue) => {
    const shouldBeChecked = newValue === 'Y'
    if (internalChecked.value !== shouldBeChecked) {
      internalChecked.value = shouldBeChecked
    }
  },
  { immediate: true }
)

// 내부 상태 변경 시 modelValue로 emit
watch(internalChecked, (newChecked) => {
  const value = newChecked ? 'Y' : 'N'
  if (value !== props.modelValue) {
    emit('update:modelValue', value)
    emit('change', value)
  }
})
</script>

<template>
  <span
    class="inline-flex items-center gap-2"
    :class="{ 'opacity-50': props.disabled }"
  >
    <Switch
      :id="props.id"
      v-model="internalChecked"
      :disabled="props.disabled"
      :name="props.name"
      :class="computedClass"
    />
    <span
      v-if="props.showStateLabel"
      class="text-xs select-none transition-colors"
      :class="internalChecked ? 'text-primary font-medium' : 'text-muted-foreground'"
    >
      {{ internalChecked ? props.activeLabel : props.inactiveLabel }}
    </span>
  </span>
</template>
